import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { app, authentication } from '@microsoft/teams-js';

// When the SPA is loaded inside a Teams tab (iframe), we initialise the Teams JS SDK and
// silently request an SSO token. The token isn't actually consumed by the SWA-linked
// Function backend yet — `staticwebapp.config.json` enforces SWA built-in auth — but
// having the token available unblocks future work (passing it on `Authorization: Bearer`
// when SWA-managed auth is bypassed). The hook returns null whenever we're not running
// in Teams, so the rest of the app sees the standard SWA principal flow.
interface TeamsContextValue {
  isInTeams: boolean;
  ssoToken: string | null;
  initError: string | null;
}

const Ctx = createContext<TeamsContextValue>({ isInTeams: false, ssoToken: null, initError: null });

export const useTeamsContext = () => useContext(Ctx);

export function TeamsContextProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<TeamsContextValue>({ isInTeams: false, ssoToken: null, initError: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await app.initialize();
        if (cancelled) return;
        // initialize() resolves only when running inside a Teams host — outside Teams
        // it rejects, which we catch below and treat as "not in Teams".
        try {
          const token = await authentication.getAuthToken();
          if (!cancelled) setValue({ isInTeams: true, ssoToken: token, initError: null });
        } catch (tokenErr) {
          if (!cancelled) {
            setValue({
              isInTeams: true,
              ssoToken: null,
              initError: tokenErr instanceof Error ? tokenErr.message : 'Could not obtain Teams SSO token.',
            });
          }
        }
      } catch {
        // Running outside Teams — leave defaults in place.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
